import { Children, isValidElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export const useCarousel = (children: ReactNode) => {
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const slides = useMemo(() => {
    const directChildren = Children.toArray(children);

    if (directChildren.length !== 1) {
      return directChildren;
    }

    const [onlyChild] = directChildren;

    if (!isValidElement<{ children?: ReactNode }>(onlyChild)) {
      return directChildren;
    }

    const nestedChildren = Children.toArray(onlyChild.props.children);

    return nestedChildren.length > 1 ? nestedChildren : directChildren;
  }, [children]);

  const hasMultipleSlides = slides.length > 1;

  const scrollToSlide = (index: number) => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    track.scrollTo({
      left: index * track.clientWidth,
      behavior: 'smooth',
    });

    setActiveIndex(index);
  };

  const handlePrevious = () => {
    scrollToSlide(activeIndex === 0 ? slides.length - 1 : activeIndex - 1);
  };

  const handleNext = () => {
    scrollToSlide(activeIndex === slides.length - 1 ? 0 : activeIndex + 1);
  };

  useEffect(() => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    const handleScroll = () => {
      if (track.clientWidth === 0) {
        return;
      }

      const nextIndex = Math.round(track.scrollLeft / track.clientWidth);

      setActiveIndex(nextIndex);
    };

    track.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      track.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackRef,
    activeIndex,
    slides,
    hasMultipleSlides,
    scrollToSlide,
    handlePrevious,
    handleNext,
  };
};
